import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that re-renders variant-specific content using the Section Rendering API
 * Tracks both variant changes and recharge selling plan changes
 */
class VariantContent extends Component {
  /** @type {string|number|null} */
  #currentVariantId = null;

  /** @type {string|number|null} */
  #currentSellingPlanId = null;

  /** @type {boolean} */
  #supportsRecharge = false;

  connectedCallback() {
    super.connectedCallback?.();

    // Store initial variant ID
    this.#currentVariantId = this.dataset.variant;

    // Listen for variant updates from variant-picker
    this.closest('.shopify-section')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate.bind(this)
    );

    // Check if this element supports recharge selling plan tracking
    this.#supportsRecharge = this.hasAttribute('recharge-subscription-updates');
    if (this.#supportsRecharge) {
      this.#initRecharge();
    }
  }

  /**
   * Handles variant update event and re-renders section content
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    this.#currentVariantId = variantId;
    this.#renderSection();
  }

  /**
   * Initialize recharge event listening
   */
  #initRecharge() {
    document.addEventListener('recharge:sellingPlanChange', this.#onSellingPlanChange.bind(this));
  }

  /**
   * Handles recharge selling plan change event
   * @param {Event} event
   */
  #onSellingPlanChange = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const { sellingPlanId } = customEvent.detail || {};
    this.#currentSellingPlanId = sellingPlanId ?? null;

    // Update data attribute so child elements can access the current selling plan
    if (this.#currentSellingPlanId != null) {
      this.dataset.sellingPlanId = String(this.#currentSellingPlanId);
    } else {
      this.dataset.sellingPlanId = '';
    }

    this.#renderSection();
  }

  /**
   * Fetches updated content and morphs only the children of this element
   */
  async #renderSection() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || !this.#currentVariantId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(this.#currentVariantId));

    // Include selling plan if available
    if (this.#currentSellingPlanId) {
      url.searchParams.set('selling_plan_id', String(this.#currentSellingPlanId));
    } else {
      url.searchParams.delete('selling_plan_id');
    }

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const newVariantContent = fragment.querySelector(`#${this.id}`);

      if (!newVariantContent) {
        console.error('No variant-content element found in response');
        return;
      }

      // Morph only the children, preserving the element and its attributes
      morph(this, newVariantContent, {
        ...MORPH_OPTIONS,
        childrenOnly: true,
      });

      // Show/hide this element based on whether new content was rendered
      this.style.display = this.children.length === 0 ? 'none' : '';

      // Trigger updatedCallback on child components since they were morphed in place
      this.querySelectorAll('[data-component-update]').forEach((child) => {
        if (typeof child.updatedCallback === 'function') {
          child.updatedCallback();
        }
      });
    } catch (error) {
      console.error('Failed to render variant content:', error);
    }
  }
}

if (!customElements.get('variant-content')) {
  customElements.define('variant-content', VariantContent);
}
